/**
 * Utility for handling dates consistently across the application,
 * specifically addressing UTC vs Local (Lagos/Nigeria GMT+1) discrepancies.
 */

export const safeParseDate = (dateString: string | null | undefined): Date => {
    if (!dateString) return new Date();
    // If the string doesn't have a timezone indicator (Z or +/-), assume it's UTC
    // This handles cases where the backend might omit the 'Z'
    const hasTimezone = dateString.includes('Z') || /[+-]\d{2}:\d{2}$/.test(dateString);
    return new Date(hasTimezone ? dateString : `${dateString}${dateString.includes('T') ? 'Z' : ''}`);
};

/**
 * Formats a date string from the API (UTC) for display in the user's local time (GMT+1).
 * Use this in tables and read-only views.
 */
export const formatDateDisplay = (dateString: string | null | undefined): string => {
    if (!dateString) return 'N/A';
    const date = safeParseDate(dateString);
    return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
    }).replace(',', '');
};

/**
 * Converts a UTC ISO string from the API into a local datetime-local input format (YYYY-MM-DDTHH:mm).
 */
export const formatDateForInput = (isoDate: string | null | undefined): string => {
    if (!isoDate) return '';
    const date = safeParseDate(isoDate);
    const pad = (num: number) => num.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/**
 * Formats a local datetime-local string (YYYY-MM-DDTHH:mm) for "pretty" display
 * without applying any further timezone offsets.
 */
export const formatInputDisplay = (inputString: string | null | undefined): string => {
    if (!inputString) return 'DD/MM/YY HH:MM AM/PM';
    const [datePart, timePart] = inputString.split('T');
    if (!datePart || !timePart) return inputString;
    const [y, m, d] = datePart.split('-');
    const [hh, mm] = timePart.split(':');

    const hour = parseInt(hh);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;

    return `${d}/${m}/${y.substring(2)} ${displayHour.toString().padStart(2, '0')}:${mm} ${ampm}`;
};

/**
 * Re-parses a local datetime-local string (YYYY-MM-DDTHH:mm) into a UTC ISO string
 * for storage in the backend. Ensures local time is correctly converted to UTC.
 */
export const parseDateFromInput = (input: string | null | undefined): string => {
    if (!input) return '';
    const [datePart, timePart] = input.split('T');
    if (!datePart || !timePart) return '';
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute] = timePart.split(':').map(Number);
    // Create a local date object (browser's timezone)
    return new Date(year, month - 1, day, hour, minute).toISOString();
};
